import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle, ArrowLeft, ArrowRight, Plus, Loader2, Copy, Check } from 'lucide-react';
import { api } from '@/lib/api';
import type {
  GrGameConfigCreateResult,
  GrGameConfigDetail,
  GrGameConfigReviewStatus,
} from '@/lib/api';
import {
  useApproveGameConfig,
  useClaimRejections,
  useCloneGameConfig,
  useGameConfigs,
  usePurgeGameConfig,
  useRejectGameConfig,
  useRestoreGameConfig,
  useSoftDeleteGameConfig,
} from '../hooks';
import { RejectionTable } from '../components/RejectionTable';

// TODO: OAuth-app → game allowlist admin UI lives at the API level only for
// now (POST/GET/DELETE /v1/arches/game-rewards/admin/oauth-apps/:appId/games).
// Build a UI here when the LitCraft / app management page is added.

// 'deleted' is a UI-only filter that maps to onlyDeleted=true on the
// API and switches the row actions from Approve/Reject/Delete to
// Restore/Purge. The review_status of a soft-deleted config is
// irrelevant once it's tombstoned.
type StatusFilter = 'all' | GrGameConfigReviewStatus | 'deleted';

const STATUS_FILTERS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'deleted', label: 'Deleted' },
];

function reviewStatusBadgeVariant(
  status: GrGameConfigReviewStatus,
): 'default' | 'secondary' | 'destructive' {
  switch (status) {
    case 'approved':
      return 'default';
    case 'pending':
      return 'secondary';
    case 'rejected':
      return 'destructive';
  }
}

// Sentinel for "Start blank" in the Clone-from selector. shadcn's Select
// requires a non-empty value on each <SelectItem>, so we can't use ''.
const CLONE_NONE = '__none__';

export function AdminGamesPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const listOptions =
    statusFilter === 'all'
      ? undefined
      : statusFilter === 'deleted'
        ? { onlyDeleted: true }
        : { reviewStatus: statusFilter };
  const { gameConfigs, loading, error, refresh } = useGameConfigs(listOptions);
  const cloneMutation = useCloneGameConfig();
  const approveMutation = useApproveGameConfig();
  const rejectMutation = useRejectGameConfig();
  const softDeleteMutation = useSoftDeleteGameConfig();
  const restoreMutation = useRestoreGameConfig();
  const purgeMutation = usePurgeGameConfig();
  const [actingId, setActingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [rejectConfirm, setRejectConfirm] = useState<GrGameConfigDetail | null>(null);
  const [softDeleteConfirm, setSoftDeleteConfirm] = useState<GrGameConfigDetail | null>(null);
  const [restoreConfirm, setRestoreConfirm] = useState<GrGameConfigDetail | null>(null);
  const [purgeConfirm, setPurgeConfirm] = useState<GrGameConfigDetail | null>(null);
  const [purgeTypedName, setPurgeTypedName] = useState('');
  const [purgeForce, setPurgeForce] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  // Clone-source list is gated on dialogOpen (review I15) — at scale a flat
  // /admin/games response can be hundreds of KB, and the dialog is closed for
  // the vast majority of page lifetime.
  const { gameConfigs: allGameConfigs } = useGameConfigs({ enabled: dialogOpen });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdGame, setCreatedGame] = useState<
    (GrGameConfigCreateResult & { rulesCopied?: number }) | null
  >(null);
  const [copied, setCopied] = useState(false);

  // Form state
  const [cloneSourceId, setCloneSourceId] = useState<string>(CLONE_NONE);
  const [gameName, setGameName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [minScore, setMinScore] = useState('0');
  const [maxScore, setMaxScore] = useState('100');

  // Locale-aware case-insensitive sort by displayName. Stable: same input
  // always produces same order — important for screen-reader users navigating
  // by row.
  const sortedGameConfigs = useMemo(
    () =>
      [...gameConfigs].sort((a, b) =>
        a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' }),
      ),
    [gameConfigs],
  );

  // Clone-source dropdown options sorted the same way.
  const cloneSourceOptions = useMemo(
    () =>
      [...allGameConfigs].sort((a, b) =>
        a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' }),
      ),
    [allGameConfigs],
  );

  const resetForm = () => {
    setCloneSourceId(CLONE_NONE);
    setGameName('');
    setDisplayName('');
    setDescription('');
    setMinScore('0');
    setMaxScore('100');
    setCreateError(null);
    setCreatedGame(null);
    setCopied(false);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!gameName.trim() || !displayName.trim()) return;

    setCreating(true);
    setCreateError(null);
    try {
      if (cloneSourceId !== CLONE_NONE) {
        // Clone path: server copies every config field + reward rules. The
        // body fields (description / minScore / maxScore) below are ignored
        // for clones — keep the form fields disabled when a source is picked
        // so the admin doesn't expect them to override the clone.
        const result = await cloneMutation.mutateAsync({
          sourceGameConfigId: cloneSourceId,
          gameName: gameName.trim(),
          displayName: displayName.trim(),
        });
        setCreatedGame({
          gameConfigId: result.gameConfigId,
          gameName: result.gameName,
          displayName: result.displayName,
          apiKey: result.apiKey,
          apiKeyPrefix: result.apiKeyPrefix,
          rulesCopied: result.rulesCopied,
        });
      } else {
        const result = await api.createGameRewardsGame({
          gameName: gameName.trim(),
          displayName: displayName.trim(),
          description: description.trim() || undefined,
          minScore: Number(minScore),
          maxScore: Number(maxScore),
        });
        setCreatedGame(result);
      }
      refresh();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create game');
    } finally {
      setCreating(false);
    }
  };

  const handleCopyApiKey = async () => {
    if (!createdGame?.apiKey) return;
    try {
      await navigator.clipboard.writeText(createdGame.apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may be unavailable in some contexts (e.g. non-HTTPS)
      setCreateError('Failed to copy to clipboard. Please copy the key manually.');
    }
  };

  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  const handleApprove = async (gameConfigId: string) => {
    setActingId(gameConfigId);
    setActionError(null);
    try {
      await approveMutation.mutateAsync({ gameConfigId });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to approve game');
    } finally {
      setActingId(null);
    }
  };

  const handleRejectConfirmed = async () => {
    if (!rejectConfirm) return;
    const gameConfigId = rejectConfirm.gameConfigId;
    setActingId(gameConfigId);
    setActionError(null);
    try {
      await rejectMutation.mutateAsync({ gameConfigId });
      setRejectConfirm(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to reject game');
    } finally {
      setActingId(null);
    }
  };

  const handleSoftDeleteConfirmed = async () => {
    if (!softDeleteConfirm) return;
    const gameConfigId = softDeleteConfirm.gameConfigId;
    setActingId(gameConfigId);
    setActionError(null);
    try {
      await softDeleteMutation.mutateAsync({ gameConfigId });
      setSoftDeleteConfirm(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete game');
    } finally {
      setActingId(null);
    }
  };

  const handleRestoreConfirmed = async () => {
    if (!restoreConfirm) return;
    const gameConfigId = restoreConfirm.gameConfigId;
    setActingId(gameConfigId);
    setActionError(null);
    try {
      await restoreMutation.mutateAsync({ gameConfigId });
      setRestoreConfirm(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to restore game');
    } finally {
      setActingId(null);
    }
  };

  const handlePurgeConfirmed = async () => {
    if (!purgeConfirm) return;
    // Defense-in-depth: the Purge button is disabled until names match,
    // but re-check here so an enabled-button race can't slip through.
    if (purgeTypedName !== purgeConfirm.gameName) return;
    const gameConfigId = purgeConfirm.gameConfigId;
    setActingId(gameConfigId);
    setActionError(null);
    try {
      await purgeMutation.mutateAsync({
        gameConfigId,
        confirmGameName: purgeTypedName,
        force: purgeForce,
      });
      setPurgeConfirm(null);
      setPurgeTypedName('');
      setPurgeForce(false);
    } catch (err) {
      // The server's row-count guard returns a message like "Refusing to
      // purge: 12345 gr_reward_log rows exceeds threshold of 10000.
      // Re-submit with force=true to confirm." We surface that verbatim
      // — the admin can then tick the force box and re-click.
      setActionError(err instanceof Error ? err.message : 'Failed to purge game');
    } finally {
      setActingId(null);
    }
  };

  const isCloning = cloneSourceId !== CLONE_NONE;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to="/a/arche_game_rewards/admin"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Admin
        </Link>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Game Configurations</h1>
          <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Register Game
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{createdGame ? 'Game Registered' : 'Register New Game'}</DialogTitle>
              </DialogHeader>

              {createdGame ? (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Game <strong>{createdGame.gameName}</strong> has been registered. It starts in{' '}
                    <strong>pending</strong> review and cannot grant rewards until approved.
                    {typeof createdGame.rulesCopied === 'number' && createdGame.rulesCopied > 0 ? (
                      <>
                        {' '}
                        Cloned <strong>{createdGame.rulesCopied}</strong> reward rule
                        {createdGame.rulesCopied === 1 ? '' : 's'} from the source.
                      </>
                    ) : null}{' '}
                    Copy the API key now — it will not be shown again.
                  </p>
                  <div className="space-y-2">
                    <Label>API Key</Label>
                    <div className="flex gap-2">
                      <Input readOnly value={createdGame.apiKey} className="font-mono text-xs" />
                      <Button variant="outline" size="icon" onClick={handleCopyApiKey}>
                        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Prefix: {createdGame.apiKeyPrefix}
                    </p>
                  </div>
                  <Button className="w-full" onClick={() => handleDialogClose(false)}>
                    Done
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleCreate} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="cloneSource">Clone settings from existing game</Label>
                    <Select value={cloneSourceId} onValueChange={setCloneSourceId}>
                      <SelectTrigger
                        id="cloneSource"
                        aria-label="Clone settings from existing game"
                      >
                        <SelectValue placeholder="Start with blank settings" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={CLONE_NONE}>Start with blank settings</SelectItem>
                        {cloneSourceOptions.map((g) => (
                          <SelectItem key={g.gameConfigId} value={g.gameConfigId}>
                            {g.displayName} ({g.gameName})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isCloning ? (
                      <p className="text-xs text-muted-foreground">
                        All settings + reward rules will be copied from the source. The clone starts
                        pending until you approve it.
                      </p>
                    ) : null}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="gameName">Game Name *</Label>
                    <Input
                      id="gameName"
                      value={gameName}
                      onChange={(e) => setGameName(e.target.value)}
                      placeholder="my_game"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="displayName">Display Name *</Label>
                    <Input
                      id="displayName"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="My Game"
                      required
                    />
                  </div>
                  {!isCloning && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                          id="description"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          placeholder="Optional description"
                          rows={3}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="minScore">Min Score</Label>
                          <Input
                            id="minScore"
                            type="number"
                            value={minScore}
                            onChange={(e) => setMinScore(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="maxScore">Max Score</Label>
                          <Input
                            id="maxScore"
                            type="number"
                            value={maxScore}
                            onChange={(e) => setMaxScore(e.target.value)}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {createError && <p className="text-sm text-destructive">{createError}</p>}

                  <Button type="submit" className="w-full" disabled={creating}>
                    {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    {isCloning ? 'Clone Game' : 'Register Game'}
                  </Button>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap" role="group" aria-label="Filter by review status">
        {STATUS_FILTERS.map((f) => (
          <Button
            key={f.value}
            variant={statusFilter === f.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(f.value)}
            aria-pressed={statusFilter === f.value}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-3">
          <AlertCircle className="h-4 w-4 text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {actionError && (
        <div className="flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-3">
          <AlertCircle className="h-4 w-4 text-destructive" />
          <p className="text-sm text-destructive">{actionError}</p>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-lg" />
          ))}
        </div>
      ) : sortedGameConfigs.length === 0 ? (
        <p className="text-center text-muted-foreground py-12">
          {statusFilter === 'all'
            ? 'No games registered yet. Click "Register Game" to get started.'
            : statusFilter === 'deleted'
              ? 'No soft-deleted games.'
              : `No ${statusFilter} games.`}
        </p>
      ) : (
        <div className="space-y-3">
          {sortedGameConfigs.map((config) => {
            const isActing = actingId === config.gameConfigId;
            return (
              <Card key={config.gameConfigId} className="hover:bg-muted/50 transition-colors">
                <CardContent className="flex items-center justify-between py-4 gap-4">
                  <Link
                    to={`/a/arche_game_rewards/admin/games/${config.gameConfigId}`}
                    className="flex-1 space-y-1"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium">{config.displayName}</p>
                      <Badge variant={config.isActive ? 'default' : 'secondary'}>
                        {config.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      <Badge
                        variant={reviewStatusBadgeVariant(config.reviewStatus)}
                        data-testid={`review-status-${config.gameConfigId}`}
                      >
                        {config.reviewStatus}
                      </Badge>
                      {config.deletedAt && (
                        <Badge variant="destructive" aria-label="Deleted">
                          deleted
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {config.gameName} | Key prefix: {config.apiKeyPrefix} | Score range:{' '}
                      {config.minScore}-{config.maxScore}
                      {config.rewardMode === 'item_list' && ' | Mode: Item List'}
                    </p>
                  </Link>

                  <div className="flex items-center gap-2">
                    {config.deletedAt ? (
                      // Soft-deleted row: only Restore + Purge are
                      // meaningful. Approve/Reject/Delete are hidden so
                      // we don't suggest editable state on a tombstone.
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setRestoreConfirm(config)}
                        disabled={isActing}
                        aria-label={`Restore ${config.displayName}`}
                      >
                        Restore
                      </Button>
                    ) : (
                      <>
                        {config.reviewStatus === 'pending' && (
                          <Button
                            size="sm"
                            onClick={() => handleApprove(config.gameConfigId)}
                            disabled={isActing}
                            aria-label={`Approve ${config.displayName}`}
                          >
                            {isActing && approveMutation.isPending ? (
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            ) : null}
                            Approve
                          </Button>
                        )}
                        {config.reviewStatus !== 'rejected' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setRejectConfirm(config)}
                            disabled={isActing}
                            aria-label={`Reject ${config.displayName}`}
                          >
                            Reject
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSoftDeleteConfirm(config)}
                          disabled={isActing}
                          aria-label={`Delete ${config.displayName}`}
                        >
                          Delete
                        </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => {
                        setPurgeTypedName('');
                        setPurgeForce(false);
                        setPurgeConfirm(config);
                      }}
                      disabled={isActing}
                      aria-label={`Purge ${config.displayName}`}
                    >
                      Purge
                    </Button>
                  </div>

                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <RecentRejectionsCard />

      <Dialog
        open={rejectConfirm !== null}
        onOpenChange={(open) => {
          if (!open && !rejectMutation.isPending) setRejectConfirm(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reject this game?</DialogTitle>
          </DialogHeader>
          {rejectConfirm && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                <strong>{rejectConfirm.displayName}</strong> ({rejectConfirm.gameName}) will move to{' '}
                <strong>rejected</strong>. New claims will be denied and the game will stop granting
                rewards.
                {rejectConfirm.reviewStatus === 'approved' && (
                  <>
                    {' '}
                    It is currently <strong>approved</strong> — already-granted rewards are not
                    affected.
                  </>
                )}
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setRejectConfirm(null)}
                  disabled={rejectMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleRejectConfirmed}
                  disabled={rejectMutation.isPending}
                >
                  {rejectMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Reject
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={softDeleteConfirm !== null}
        onOpenChange={(open) => {
          if (!open && !softDeleteMutation.isPending) setSoftDeleteConfirm(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete this game?</DialogTitle>
          </DialogHeader>
          {softDeleteConfirm && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                <strong>{softDeleteConfirm.displayName}</strong> ({softDeleteConfirm.gameName}) will
                be hidden from this list and from the claim auth path. Historical reward-log entries
                remain queryable. Reversing this requires direct database intervention — use{' '}
                <strong>Purge</strong> instead if you want it gone permanently.
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setSoftDeleteConfirm(null)}
                  disabled={softDeleteMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleSoftDeleteConfirmed}
                  disabled={softDeleteMutation.isPending}
                >
                  {softDeleteMutation.isPending && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={restoreConfirm !== null}
        onOpenChange={(open) => {
          if (!open && !restoreMutation.isPending) setRestoreConfirm(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Restore this game?</DialogTitle>
          </DialogHeader>
          {restoreConfirm && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                <strong>{restoreConfirm.displayName}</strong> ({restoreConfirm.gameName}) will
                reappear in admin lists and accept claims again. Its review status (
                <strong>{restoreConfirm.reviewStatus}</strong>) is preserved.
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setRestoreConfirm(null)}
                  disabled={restoreMutation.isPending}
                >
                  Cancel
                </Button>
                <Button onClick={handleRestoreConfirmed} disabled={restoreMutation.isPending}>
                  {restoreMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Restore
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={purgeConfirm !== null}
        onOpenChange={(open) => {
          if (!open && !purgeMutation.isPending) {
            setPurgeConfirm(null);
            setPurgeTypedName('');
            setPurgeForce(false);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Purge this game permanently?</DialogTitle>
          </DialogHeader>
          {purgeConfirm && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                <strong>{purgeConfirm.displayName}</strong> and every reward-log entry, reward rule,
                recipe, leaderboard entry, and time-played row for it will be{' '}
                <strong>permanently deleted</strong>. This cannot be undone.
              </p>
              <div className="space-y-2">
                <Label htmlFor="purge-confirm">
                  Type the game name <span className="font-mono">{purgeConfirm.gameName}</span> to
                  confirm:
                </Label>
                <Input
                  id="purge-confirm"
                  value={purgeTypedName}
                  onChange={(e) => setPurgeTypedName(e.target.value)}
                  placeholder={purgeConfirm.gameName}
                  autoComplete="off"
                />
              </div>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={purgeForce}
                  onChange={(e) => setPurgeForce(e.target.checked)}
                  className="mt-1"
                  aria-label="Force purge — override the reward-log row-count guard"
                />
                <span className="text-muted-foreground">
                  Force purge even if the reward-log row count exceeds the safety threshold. Tick
                  this only after the server rejects a normal purge.
                </span>
              </label>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setPurgeConfirm(null);
                    setPurgeTypedName('');
                    setPurgeForce(false);
                  }}
                  disabled={purgeMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handlePurgeConfirmed}
                  disabled={purgeMutation.isPending || purgeTypedName !== purgeConfirm.gameName}
                >
                  {purgeMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Purge permanently
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Read-only panel showing the most recent claim rejections. Helps the
 * platform admin see what arches/apps tried to claim against unregistered or
 * unapproved games — surfaces the exact game name / event / score the caller
 * sent so the admin can register a matching gr_game_configs row.
 */
// 30 days = 720h. Single source of truth for the AdminGamesPage panel and the
// AdminRewardLogPage tab ranges (review I6).
const RECENT_REJECTIONS_HOURS = 720;

function RecentRejectionsCard() {
  const { rejections, loading, error } = useClaimRejections({
    sinceHours: RECENT_REJECTIONS_HOURS,
    limit: 25,
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Recent Claim Rejections</CardTitle>
        <Link
          to="/a/arche_game_rewards/admin/reward-log"
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          View all →
        </Link>
      </CardHeader>
      <CardContent>
        {error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : loading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-8 rounded" />
            ))}
          </div>
        ) : rejections.length === 0 ? (
          <p className="text-sm text-muted-foreground">No rejected claims in the past 30 days.</p>
        ) : (
          <RejectionTable entries={rejections} compact />
        )}
      </CardContent>
    </Card>
  );
}
